import { useEffect } from 'react';
import { renderImage, renderParagraph } from 'components/utils/render';
import { pushClickTileEvent } from '../../../tracking/events';
import { captureInboundAttribution, appendTileUtms, getTileHref, getTileId, isOutboundUrl } from '../../../tracking/tiles';
import { registerMixpanelSuperProperties } from '../../../tracking/mixpanel';
import { WayCard as WayCardData } from '../AwarenessRaiserPage/AwarenessRaiserPage.models';
import { AwarenessRaiserGrowAMoPageProps, FeatureBlock as FeatureBlockData } from './AwarenessRaiserGrowAMoPage.models';
import {
    HeroSection,
    HeroMedia,
    HeroCopy,
    Eyebrow,
    HeroTitle,
    HeroText,
    FeatureSection,
    FeatureInner,
    FeatureLead,
    FeatureHeading,
    FeatureIntro,
    FeatureItem,
    FeatureItemHeading,
    FeatureItemText,
    TestimonialsSection,
    SectionInner,
    TestimonialsHeading,
    TestimonialsGrid,
    TestimonialCard,
    TestimonialMedia,
    TestimonialQuote,
    TestimonialName,
    WaysSection,
    WaysInner,
    WaysEyebrow,
    WaysHeading,
    WaysGrid,
    WayCard,
    WayCardImage,
    WayCardTitle,
    WayCardCta,
} from './AwarenessRaiserGrowAMoPage.style';

/**
 * Awareness Raiser Grow A Mo Page (DFE-98).
 *
 * One of the six ways to raise awareness the landing page unlocks, built as its
 * own page: a split hero, two dark bands of columns, community quotes, then the
 * ways grid.
 *
 * The ways grid is the same CMS section and the same transformer output as the
 * landing page's, but it is always visible here rather than revealed on sign up:
 * a reader who reached this page has already signed up, so there is nothing to
 * gate. That means no localStorage read and no sign up state on this page at
 * all.
 *
 * Sections the content editor left empty are omitted rather than rendered blank.
 */

// Drawn from the design's own geometry: remixicon ships filled glyphs, which
// read heavier than this stroked one.
const Chevron = () => (
    <svg
        viewBox="0 0 24 24"
        width="16"
        height="16"
        fill="none"
        stroke="currentColor"
        strokeWidth={3}
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
        focusable="false"
    >
        <path d="M9 5l7 7-7 7" />
    </svg>
);

// The card is a plain anchor rather than the shared Button, so the CTA click
// event it would have fired is raised here instead.
const trackWayCard = (card: WayCardData, index: number) => {
    if (!card.cta) return;
    const tileId = getTileId(card);
    const destinationUrl = appendTileUtms(getTileHref(card), tileId);
    pushClickTileEvent({
        linkText: card.cta.btnText,
        linkUrl: destinationUrl,
        tileId,
        position: index + 1,
        isOutbound: isOutboundUrl(destinationUrl),
        pledgeContext: 'ungated',
    });
};

// The landing page's pledge flag. This page cannot pledge, so it only reads it.
const SIGNED_UP_STORAGE_KEY = 'mo-awareness-landing-signed-up';

const readSignedUp = () => {
    try {
        return window.localStorage.getItem(SIGNED_UP_STORAGE_KEY) === 'true';
    } catch (e) {
        return false;
    }
};

const hasImage = (image?: object) => !!image && Object.keys(image).length > 0;

const hasBlock = (block?: Array<any>) => !!block && block.length > 0;

/**
 * Both dark bands are the same shape, so they render through one local
 * component rather than being written out twice.
 */
const FeatureBlock: React.FC<{ block: FeatureBlockData }> = ({ block }) => {
    const items = block.items || [];

    return (
        <FeatureSection>
            <FeatureInner>
                <FeatureLead>
                    {block.heading && <FeatureHeading>{block.heading}</FeatureHeading>}
                    {hasBlock(block.intro) && <FeatureIntro>{renderParagraph(block.intro)}</FeatureIntro>}
                </FeatureLead>
                {items.map((item, index) =>
                    <FeatureItem key={index}>
                        {item.heading && <FeatureItemHeading>{item.heading}</FeatureItemHeading>}
                        {hasBlock(item.text) && <FeatureItemText>{renderParagraph(item.text)}</FeatureItemText>}
                    </FeatureItem>
                )}
            </FeatureInner>
        </FeatureSection>
    );
};

const isRenderableBlock = (block?: FeatureBlockData | null) =>
    !!block && (!!block.heading || hasBlock(block.intro) || (block.items || []).length > 0);

const AwarenessRaiserGrowAMoPage: React.FC<AwarenessRaiserGrowAMoPageProps> = ({
    image,
    eyebrow,
    heroTitle,
    heroText,
    whyBlock,
    testimonials,
    tipsBlock,
    ways,
}) => {
    const testimonialCards = (testimonials && testimonials.cards) || [];
    const wayCards = (ways && ways.cards) || [];

    useEffect(() => {
        captureInboundAttribution();
    }, []);

    useEffect(() => {
        if (readSignedUp()) {
            registerMixpanelSuperProperties({ awareness_raiser: true });
        }
    }, []);

    return (
        <>
            {(hasImage(image) || heroTitle || eyebrow || hasBlock(heroText)) &&
                <HeroSection>
                    {hasImage(image) &&
                        <HeroMedia>{renderImage(image)}</HeroMedia>
                    }
                    <HeroCopy>
                        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
                        {heroTitle && <HeroTitle>{heroTitle}</HeroTitle>}
                        {hasBlock(heroText) && <HeroText>{renderParagraph(heroText)}</HeroText>}
                    </HeroCopy>
                </HeroSection>
            }

            {isRenderableBlock(whyBlock) && <FeatureBlock block={whyBlock as FeatureBlockData} />}

            {testimonialCards.length > 0 &&
                <TestimonialsSection>
                    <SectionInner>
                        {testimonials && testimonials.heading &&
                            <TestimonialsHeading>{testimonials.heading}</TestimonialsHeading>
                        }
                        <TestimonialsGrid>
                            {testimonialCards.map((card, index) =>
                                <TestimonialCard key={index}>
                                    {hasImage(card.image) &&
                                        <TestimonialMedia>{renderImage(card.image)}</TestimonialMedia>
                                    }
                                    {hasBlock(card.quote) &&
                                        <TestimonialQuote>{renderParagraph(card.quote)}</TestimonialQuote>
                                    }
                                    {card.name && <TestimonialName>{card.name}</TestimonialName>}
                                </TestimonialCard>
                            )}
                        </TestimonialsGrid>
                    </SectionInner>
                </TestimonialsSection>
            }

            {isRenderableBlock(tipsBlock) && <FeatureBlock block={tipsBlock as FeatureBlockData} />}

            {wayCards.length > 0 &&
                <WaysSection>
                    <WaysInner>
                        {ways && ways.eyebrow && <WaysEyebrow>{ways.eyebrow}</WaysEyebrow>}
                        {ways && ways.heading && <WaysHeading>{ways.heading}</WaysHeading>}
                        <WaysGrid>
                            {wayCards.map((card, index) =>
                                <WayCard
                                    key={index}
                                    href={card.cta ? appendTileUtms(getTileHref(card), getTileId(card)) : undefined}
                                    aria-label={card.cta ? `${card.title} — ${card.cta.btnText}` : card.title}
                                    onClick={() => trackWayCard(card, index)}
                                    {...(card.cta && card.cta.btnType === 'external'
                                        ? { target: '_blank', rel: 'noopener' }
                                        : {})}
                                >
                                    {hasImage(card.image) &&
                                        <WayCardImage>{renderImage(card.image)}</WayCardImage>
                                    }
                                    {card.title && <WayCardTitle>{card.title}</WayCardTitle>}
                                    {card.cta && card.cta.btnText &&
                                        <WayCardCta>
                                            {card.cta.btnText}
                                            <Chevron />
                                        </WayCardCta>
                                    }
                                </WayCard>
                            )}
                        </WaysGrid>
                    </WaysInner>
                </WaysSection>
            }
        </>
    );
};

export default AwarenessRaiserGrowAMoPage;
